import { useCallback, useEffect, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search-and-page an Account option list.
 *
 * @param {Function} fetchPage (query, offset) => {accounts, total}
 * @param {boolean} isOpen whether the picker is showing; the first page waits
 *   for it. Pass `true` for a list that is always on screen, such as the users
 *   list's filter row.
 * @param {number} [refreshKey] bump to re-ask for the first page. The current
 *   search term is kept, so a refresh re-asks the same question rather than
 *   silently widening a list the user had narrowed.
 */
const useAccountSearch = (fetchPage, isOpen, refreshKey = 0) => {
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  const queryRef = useRef('');

  const requestId = useRef(0);

  const loadPage = useCallback(
    async ({ append, query, offset }) => {
      const id = requestId.current + 1;
      requestId.current = id;
      queryRef.current = query;
      setIsLoadingAccounts(true);

      try {
        const { accounts, total } = await fetchPage(query, offset);
        // Superseded by a newer request. Not a failure, so it reports success:
        // the newer request owns the outcome.
        if (requestId.current !== id) return true;

        setAccountOptions((current) => {
          if (!append) return accounts;

          const seen = new Set(current.map((option) => String(option.value)));
          return [
            ...current,
            ...accounts.filter((option) => !seen.has(String(option.value))),
          ];
        });
        setAccountsTotal(total);
        return true;
      } catch {
        if (requestId.current !== id) return true;

        if (!append) {
          setAccountOptions([]);
          setAccountsTotal(0);
        }

        // Reported rather than thrown: every caller is fire-and-forget, and an
        // unhandled rejection out of a debounce timer has nowhere to land. The
        // first-page effect uses it to allow a retry.
        return false;
      } finally {
        if (requestId.current === id) setIsLoadingAccounts(false);
      }
    },
    [fetchPage],
  );

  // Which refreshKey the loaded first page belongs to; null when nothing is
  // loaded. Going through loadPage rather than fetching here is what makes a
  // refresh safe: its requestId guard keeps an in-flight page from landing on
  // top of the fresh one.
  const loadedKey = useRef(null);

  const pendingFirstLoad = useRef(false);

  useEffect(() => {
    if (!isOpen || loadedKey.current === refreshKey) return undefined;

    loadedKey.current = refreshKey;
    pendingFirstLoad.current = true;

    loadPage({ append: false, query: queryRef.current, offset: 0 }).then(
      (loaded) => {
        pendingFirstLoad.current = false;
        // A failed first page empties the list; leaving loadedKey unset lets the
        // next open ask again rather than stranding an empty menu.
        if (!loaded && loadedKey.current === refreshKey)
          loadedKey.current = null;
      },
    );

    return undefined;
  }, [isOpen, loadPage, refreshKey]);

  // Closing before the first page arrives discards it. Bumping requestId is what
  // makes that stick: the response would otherwise land in a menu nobody is
  // looking at, leaving it loaded from a question the user has since abandoned.
  // Clearing loadedKey lets the next open ask again.
  useEffect(() => {
    if (isOpen || !pendingFirstLoad.current) return;

    pendingFirstLoad.current = false;
    requestId.current += 1;
    loadedKey.current = null;
    setIsLoadingAccounts(false);
  }, [isOpen]);

  const searchTimer = useRef(null);

  useEffect(() => {
    if (!isOpen && searchTimer.current) clearTimeout(searchTimer.current);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [isOpen]);

  const searchAccounts = useCallback(
    (query) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);

      // Assigned before the debounce rather than inside loadPage: a refreshKey
      // bump landing inside the 300ms window would otherwise re-ask the first
      // page with the term the user has already typed past.
      queryRef.current = query;

      searchTimer.current = setTimeout(() => {
        loadPage({ append: false, query, offset: 0 });
      }, SEARCH_DEBOUNCE_MS);
    },
    [loadPage],
  );

  const hasMoreAccounts = accountOptions.length < accountsTotal;

  const loadMoreAccounts = useCallback(() => {
    if (isLoadingAccounts) return;
    if (accountOptions.length >= accountsTotal) return;

    loadPage({
      append: true,
      query: queryRef.current,
      offset: accountOptions.length,
    });
  }, [accountOptions.length, accountsTotal, isLoadingAccounts, loadPage]);

  return {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    searchAccounts,
  };
};

export default useAccountSearch;
